/* Technical Audit & Specification — rendered inside the Partner demo. */

const SIGNAL_ROWS = [
  ["transaction_amount", "Size vs supplier's same-currency history (ratio to average)", "≤1.5x / 1.5–3x / >3x", "no prior same-currency history"],
  ["spend_change", "Total spend, current 30-day period vs previous", "≤50% / 50–200% / >200%", "no spend in previous period"],
  ["data_quality", "Completeness/plausibility of supplier & transaction fields", "≤20 / 21–50 / >50", "never"],
  ["category_consistency", "Missing or inconsistent category vs history", "consistent / missing / new for established", "never"],
  ["supplier_activity", "Transaction frequency, current period vs previous", "≤50% / 50–200% / >200%", "no activity in previous period"],
];

export default function AuditPanel() {
  return (
    <section className="sw-audit" data-testid="audit-panel">
      <div className="sw-section-head">
        <span className="sw-kicker">Technical Audit & Specification</span>
        <h3 className="sw-h2">How the signal is computed — in full.</h3>
      </div>
      <p className="sw-audit-meta">
        Document v1.0 · signal engine 2026.06.2 · fusion fusion-1.0.0 · audit June 2026
      </p>

      <div className="sw-audit-body">
        <h4 className="sw-audit-h">1 · Deterministic by design</h4>
        <p>
          Every value is reproducible from the same input and methodology version. No machine
          learning, no randomness, no wall-clock reads inside scoring. Time windows are measured
          relative to the event's transaction date.
        </p>

        <h4 className="sw-audit-h">2 · The five signals</h4>
        <div className="sw-table-wrap">
          <table className="sw-table">
            <thead>
              <tr><th>Signal</th><th>Measures</th><th>NORMAL / WATCH / REVIEW</th><th>Skipped when</th></tr>
            </thead>
            <tbody>
              {SIGNAL_ROWS.map((r) => (
                <tr key={r[0]}>
                  <td className="sw-mono">{r[0]}</td>
                  <td>{r[1]}</td>
                  <td className="sw-mono">{r[2]}</td>
                  <td>{r[3]}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <h4 className="sw-audit-h">3 · Insufficient-data handling</h4>
        <p>
          A signal that cannot be computed is returned with <code>applicable: false</code> and the
          exact message <em>"Insufficient data to calculate this signal."</em> It is never given an
          estimated or placeholder score, and it is excluded from the fused score.
        </p>

        <h4 className="sw-audit-h">4 · Fusion & confidence</h4>
        <p>
          Applicable signals are combined with renormalized weights so a skipped signal never
          drags the score down: <code>priority_score = Σ(score × weight) / Σ(weight over applicable)</code>.
          Weights (fusion-1.0.0): amount 20, spend 25, data quality 15, category 15, activity 25.
          Bands: NORMAL 0–39, WATCH 40–69, REVIEW 70–84, HIGH PRIORITY 85–100. Confidence =
          0.6 × signal coverage + 0.4 × data completeness.
        </p>

        <h4 className="sw-audit-h">5 · Reproducibility & audit trail</h4>
        <p>
          Each payload is hashed with SHA-256 over a canonical (sorted-key) form; the methodology
          version is recorded on every result; and each response carries an evidence reference
          (input hash, retrieval URL, stored-at) so the exact result can be re-served for audit.
        </p>

        <h4 className="sw-audit-h">6 · Limitations</h4>
        <p>
          No FX normalization (comparisons are per-currency); fixed documented thresholds; new
          suppliers legitimately skip history-based signals, reflected as lower confidence.
        </p>

        <p className="sw-disclaimer">
          Signal Wrapper provides deterministic prioritization signals based on available
          transaction data only. Scores are not legal determinations, fraud assessments,
          regulatory compliance certifications, or financial advice. Results should be reviewed
          by qualified personnel before any procurement decision is made. Methodology version is
          recorded with every result for reproducibility and audit purposes.
        </p>
      </div>
    </section>
  );
}
